// utils/config.js - Frontend Configuration Utility
import api from './api';

let cachedConfig = null;

/**
 * Fetches public configuration from backend
 * Caches the result to avoid repeated API calls
 * @returns {Promise<Object>} Configuration object with emails and company info
 */
export async function getConfig() {
  if (cachedConfig) {
    return cachedConfig;
  }

  try {
    const response = await api.get('/config');
    cachedConfig = response.data;
    return cachedConfig;
  } catch (error) {
    console.error('Failed to fetch config:', error);
    // Return fallback defaults if API call fails
    return {
      company: {
        name: 'Snaptura',
        website: 'https://snaptura.online'
      },
      emails: {
        support: 'support@snaptura.online',
        sales: 'sales@snaptura.online',
        legal: 'legal@snaptura.online',
        privacy: 'privacy@snaptura.online',
        noreply: 'noreply@snaptura.online'
      }
    };
  }
}

/**
 * Get a specific email address from config
 * @param {string} type - Email type (support, sales, legal, privacy, noreply)
 * @returns {Promise<string>} Email address
 */
export async function getEmail(type) {
  const config = await getConfig();
  return config.emails[type] || `${type}@snaptura.online`;
}

/**
 * Get company information
 * @returns {Promise<Object>} Company name and website
 */
export async function getCompanyInfo() {
  const config = await getConfig();
  return config.company;
}

/**
 * Clear cached config (useful for testing or if config changes)
 */
export function clearConfigCache() {
  cachedConfig = null;
}

export default {
  getConfig,
  getEmail,
  getCompanyInfo,
  clearConfigCache
};
